import React, { useMemo, useState } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  Badge, Box, Button, Flex, Input, InputGroup, InputLeftElement, Text, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { FiClipboard, FiFileText, FiSearch } from 'react-icons/fi';
import AuroraPageShell from '../shared/AuroraPageShell';
import { APPROVED_ERA } from '../shared/ProjectHandOffsSection';
import useProposals from '../hooks/useProposals';
import openPdf from '../utils/openPdf';
import { Proposal } from '../models/proposal';

// item 471 (Chad 2026-07-05) — the all-hand-offs list: every approved proposal's numbers-free
// deployment brief in one place, reachable from Basix Six ▸ Proposals ▸ Hand-offs (mirrors
// Invoices ▸ Subscriptions). Open to any authenticated tenant user (the Team role's entry
// point — they can't open proposals but the hand-off is theirs).
const HandOffsPage: React.FC = () => {
  const { data, status } = useProposals();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const rowBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');

  const handOffs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.index ?? [])
      .filter((p) => !p.isVoid && APPROVED_ERA.includes(p.status))
      .filter((p) => !q
        || p.proposalTitle?.toLowerCase().includes(q)
        || p.proposalNumber?.toLowerCase().includes(q)
        || p.customerName?.toLowerCase().includes(q))
      .sort((a, b) => (a.proposalTitle || '').localeCompare(b.proposalTitle || ''));
  }, [data, search]);

  const view = (id: string) => {
    openPdf(`proposal/${id}/handoff-pdf`).catch(() => toast({
      status: 'error', title: 'Could not open the hand-off', duration: 6000, isClosable: true,
    }));
  };

  return (
    <AuroraPageShell
      title="HAND-OFFS"
      icon={<FiClipboard />}
      subtitle={status === 'success'
        ? `${handOffs.length} ${handOffs.length === 1 ? 'hand-off' : 'hand-offs'} from approved proposals`
        : 'Loading hand-offs…'}
    >
      <Box maxW="720px">
        <InputGroup size="sm" mb={4}>
          <InputLeftElement pointerEvents="none"><FiSearch /></InputLeftElement>
          <Input
            placeholder="Search hand-offs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            borderRadius="lg"
            variant="filled"
          />
        </InputGroup>

        {status === 'success' && handOffs.length === 0 && (
          <Text fontSize="sm" color={subColor} py={2}>
            No hand-offs yet — a proposal&apos;s hand-off appears here once it&apos;s approved.
          </Text>
        )}

        {handOffs.map((p: Proposal) => (
          <Flex key={p.id} align="center" py={3} borderBottomWidth="1px" borderBottomColor={rowBorder} gap={3}>
            <Box minW={0}>
              <Flex align="center" gap={2}>
                <Text fontSize="sm" fontWeight="semibold" isTruncated>
                  {p.proposalTitle || p.proposalNumber}
                </Text>
                <Badge colorScheme="green" variant="subtle" flexShrink={0}>APPROVED</Badge>
              </Flex>
              <Text fontSize="xs" color={subColor} isTruncated>
                {[p.proposalNumber, p.customerName].filter(Boolean).join(' · ')}
                {p.handOffNotes ? ' · has deployment notes' : ''}
              </Text>
            </Box>
            <Button
              ml="auto"
              size="xs"
              variant="outline"
              leftIcon={<FiFileText />}
              flexShrink={0}
              onClick={() => view(p.id)}
            >
              View
            </Button>
          </Flex>
        ))}
      </Box>
    </AuroraPageShell>
  );
};

export default withAuthenticationRequired(HandOffsPage);
